"use client";

import type React from "react";

import { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Button,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ExpandMore,
  Gavel,
  EmojiEvents,
  People,
  CalendarToday,
  CheckCircle,
  Info,
  Upload,
  School,
  ContactSupport,
  Warning,
  Assignment,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

import TCCDeliverablesJudgingCriteria from "../../../assets/TCC Competition Judging Criteria.pdf";

export default function RulesAndGuidelinesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [expanded, setExpanded] = useState<string | false>("panel1");
  const [expandedAll, setExpandedAll] = useState(false);
  const [expandedDeliverable, setExpandedDeliverable] = useState<
    string | false
  >(false);
  const navigate = useNavigate();
  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (!expandedAll) {
        setExpanded(isExpanded ? panel : false);
      }
    };

  const handleDeliverableChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (!expandedAll) {
        setExpandedDeliverable(isExpanded ? panel : false);
      }
    };

  const handleExportPDF = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const previousExpanded = expanded;
    const previousExpandedDeliverable = expandedDeliverable;
    setExpandedAll(true);

    await new Promise((resolve) => setTimeout(resolve, 500)); // Đợi UI cập nhật

    const accordions = document.querySelectorAll(".pdf-section"); // Lấy từng accordion

    for (let i = 0; i < accordions.length; i++) {
      const accordion = accordions[i] as HTMLElement;

      const canvas = await html2canvas(accordion, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 210; // Chiều rộng A4 (mm)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (i > 0) pdf.addPage(); // Thêm trang mới nếu không phải trang đầu tiên
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    }

    pdf.save("Rules_and_Guidelines.pdf");

    setExpandedAll(false); // Khôi phục trạng thái ban đầu
    setExpanded(previousExpanded);
    setExpandedDeliverable(previousExpandedDeliverable);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <div id="pdf-content">
        <Box className="pdf-section">
          <Box
            sx={{
              mb: 6,
              textAlign: "center",
              background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
              py: 4,
              borderRadius: 2,
              color: "white",
              boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              fontWeight="bold"
            >
              Rules & Guidelines
            </Typography>
            <Typography variant="h6">
              Pythaverse Educators ThoughtCoCreate 2025
            </Typography>
          </Box>

          <Card sx={{ mb: 4, bgcolor: "#FFF9C4" }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Info color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Important Notice
                </Typography>
              </Box>
              <Typography>
                Registration is open from April 2, 2025. All participants must
                create a free Pythaverse account. The first round of submissions
                is due by May 15, 2025.
              </Typography>
            </CardContent>
          </Card>

          {/* Section 1: Overview */}
          <Accordion
            // expanded={expanded === "panel1"}
            expanded={expandedAll || expanded === "panel1"}
            onChange={handleChange("panel1")}
            sx={{ mb: 2 }}
            // className="pdf-section"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel1-content"
              id="panel1-header"
              sx={{ bgcolor: "primary.light", color: "white" }}
            >
              <Box display="flex" alignItems="center">
                <Info sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  1. Overview
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography paragraph>
                Welcome to the Pythaverse Educators ThoughtCoCreate 2025 event.
                This initiative aims to empower individual educators worldwide
                to share, co-create, and refine solutions that transform
                teaching, learning, and school operations in the AI era. The
                guidelines below clarify how participants can register, submit
                their work, and ultimately collaborate in shaping the future of
                education.
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Section 2: Eligibility & Participation */}
          <Accordion
            // expanded={expanded === "panel2"}
            expanded={expandedAll || expanded === "panel2"}
            onChange={handleChange("panel2")}
            sx={{ mb: 2 }}
            // className="pdf-section"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel2-content"
              id="panel2-header"
              sx={{ bgcolor: "primary.light", color: "white" }}
            >
              <Box display="flex" alignItems="center">
                <People sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  2. Eligibility & Participation
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Who Can Participate
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Open to Individual Educators: Classroom teachers, administrators, curriculum designers, or education consultants from any country." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="No Team Submissions: In this 2025 edition, only individual submissions will be accepted." />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom fontWeight="bold">
                Registration Requirements
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Pythaverse Account: Participants must create a free account on the Pythaverse platform." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Event Registration: Complete the online registration form by the specified deadline." />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom fontWeight="bold">
                Participation Fee
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Free of Charge: There is no participation fee for the 2025 event, thanks to sponsor and partner support." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 3: Event Timeline */}
          <Accordion
            // expanded={expanded === "panel3"}
            expanded={expandedAll || expanded === "panel3"}
            onChange={handleChange("panel3")}
            sx={{ mb: 2 }}
            // className="pdf-section"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel3-content"
              id="panel3-header"
              sx={{ bgcolor: "primary.light", color: "white" }}
            >
              <Box display="flex" alignItems="center">
                <CalendarToday sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  3. Event Timeline
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table aria-label="event timeline table">
                  <TableHead sx={{ bgcolor: "primary.main" }}>
                    <TableRow>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Milestone
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Date
                      </TableCell>
                      {/* <TableCell sx={{ color: "white", fontWeight: "bold" }}>Description</TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* <TableRow>
                  <TableCell>Announcement</TableCell>
                  <TableCell>February 5, 2025</TableCell>
                  <TableCell>Official launch and publication of the rules & guidelines.</TableCell>
                </TableRow> */}
                    <TableRow>
                      <TableCell>Registration Period</TableCell>
                      <TableCell>April 2, 2025</TableCell>
                      {/* <TableCell>Educators register on the Pythaverse platform.</TableCell> */}
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        Deadline for all deliverables.
                      </TableCell>
                      <TableCell>May 15, 2025</TableCell>
                      {/* <TableCell>Deliverables 1, 2, 3, and 4 must be submitted.</TableCell> */}
                    </TableRow>
                    {/* <TableRow>
                  <TableCell>Round 2 Submission Deadline</TableCell>
                  <TableCell>May 1, 2025</TableCell>
                  <TableCell>Deliverable 4 (Refined Proposal) must be submitted.</TableCell>
                </TableRow> */}
                    <TableRow>
                      <TableCell>Announcing top 10</TableCell>
                      <TableCell>June 1, 2025</TableCell>
                      {/* <TableCell>Committee reviews and selects Top 10 proposals.</TableCell> */}
                    </TableRow>
                    <TableRow>
                      <TableCell>Summit for top 10 finalists</TableCell>
                      <TableCell>July 5, 2025</TableCell>
                      {/* <TableCell>Finalists attend workshops and co-creation sessions.</TableCell> */}
                    </TableRow>
                    <TableRow>
                      <TableCell>Whitepaper Publication</TableCell>
                      <TableCell>September 2025</TableCell>
                      {/* <TableCell>Official release of the compiled Whitepaper.</TableCell> */}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Box>
        {/* Section 4: Required Deliverables */}
        <Accordion
          // expanded={expanded === "panel4"}
          expanded={expandedAll || expanded === "panel4"}
          onChange={handleChange("panel4")}
          sx={{ mb: 2 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel4-content"
            id="panel4-header"
            sx={{ bgcolor: "primary.light", color: "white" }}
            className="pdf-section"
          >
            <Box display="flex" alignItems="center">
              <Assignment sx={{ mr: 2 }} />
              <Typography variant="h6" fontWeight="bold">
                4. Required Deliverables
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Accordion
              // expanded={expandedDeliverable === "deliverable1"}
              expanded={expandedAll || expandedDeliverable === "deliverable1"}
              onChange={handleDeliverableChange("deliverable1")}
              sx={{ mb: 2 }}
              className="pdf-section"
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="deliverable1-content"
                id="deliverable1-header"
                sx={{ bgcolor: "#e3f2fd" }}
              >
                <Typography fontWeight="bold">
                  Deliverable 1: Personalized Problem Statement
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="AI-Assisted: Use any recommended AI tool (e.g., OpenAI, Anthropic) to generate or refine a problem statement relevant to your school or classroom context." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Length: 150–300 words." />
                  </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Expanded Description:
                </Typography>
                <Typography paragraph>
                  For the first deliverable, we want you to pinpoint a real and
                  pressing issue you are currently facing in your classroom,
                  school, or educational environment that is related to the
                  integration or impact of Artificial Intelligence (AI). This
                  should be a challenge that you have personally observed or
                  experienced.
                </Typography>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Think about:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="What are the difficulties or obstacles you're encountering as AI becomes more prevalent in education or society?" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Where do you see potential negative impacts or missed opportunities related to AI in your specific teaching situation?" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="What are the 'pain points' or frustrations you or your colleagues are experiencing concerning AI in education?" />
                  </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Examples of Challenges:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Students becoming overly reliant on AI tools for homework and losing critical thinking skills." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Lack of teacher training and resources to effectively use AI tools in lesson planning and delivery." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Concerns about ethical implications and biases in AI-powered educational resources." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Difficulty in adapting curriculum and assessments to address the changing skills needed in an AI-driven world." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Ensuring equitable access to AI-based educational opportunities for all students, regardless of background." />
                  </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Key Outcome:
                </Typography>
                <Typography>
                  By the end of this deliverable, you will have a clearly
                  defined and personalized problem statement that sets the stage
                  for developing an AI-integrated solution in the subsequent
                  steps.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              // expanded={expandedDeliverable === "deliverable2"}
              expanded={expandedAll || expandedDeliverable === "deliverable2"}
              onChange={handleDeliverableChange("deliverable2")}
              sx={{ mb: 2 }}
              className="pdf-section"
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="deliverable2-content"
                id="deliverable2-header"
                sx={{ bgcolor: "#e3f2fd" }}
              >
                <Typography fontWeight="bold">
                  Deliverable 2: AI-Built Proposal
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="AI-Generated Draft: Prompt the AI tool to propose a preliminary solution or strategy." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Focus: Outline key actions, resources required, and expected outcomes." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Length: 300–500 words." />
                  </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Expanded Description:
                </Typography>
                <Typography paragraph>
                  In this deliverable, you will actively use Artificial
                  Intelligence (AI) tools to help you brainstorm and create an
                  initial proposal to address the problem you identified in
                  Deliverable 1. This is about exploring the potential of AI as
                  a creative partner in solution development.
                </Typography>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  How to use AI Tools:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Select an AI Tool: You can use any reputable AI tool like large language models (e.g., ChatGPT, Bard, Claude), AI brainstorming tools, or even specialized AI educational platforms." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Craft Effective Prompts: Think carefully about how to ask the AI to generate a solution. Provide context from your problem statement and be specific about what you are looking for. (e.g., 'Propose a step-by-step plan to address student over-reliance on AI for homework in a high school setting.')" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Iterate and Refine: Don't expect the first AI output to be perfect. Experiment with different prompts, ask the AI to refine its suggestions, or try generating solutions from multiple AI tools." />
                  </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Focus of your AI-Built Proposal:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Key Steps: What are the main actions or stages involved in your proposed solution? Outline a logical sequence of steps." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Resources Required: What resources (e.g., tools, materials, training, budget) would be needed to implement this solution?" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Expected Outcomes: What are the anticipated positive results or impacts of implementing this solution? What changes do you hope to see?" />
                  </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Key Outcome:
                </Typography>
                <Typography>
                  By the end of this deliverable, you will have an AI-generated
                  initial proposal that provides a foundation for your solution,
                  outlining key elements and considerations.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              // expanded={expandedDeliverable === "deliverable3"}
              expanded={expandedAll || expandedDeliverable === "deliverable3"}
              onChange={handleDeliverableChange("deliverable3")}
              sx={{ mb: 2 }}
              className="pdf-section"
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="deliverable3-content"
                id="deliverable3-header"
                sx={{ bgcolor: "#e3f2fd" }}
              >
                <Typography fontWeight="bold">
                  Deliverable 3: Critique & Analysis
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Educator-Driven: Critically evaluate the AI-built proposal." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Focus: Identify feasibility, potential pitfalls, ethical considerations, and room for improvement." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Length: 300–500 words." />
                  </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Expanded Description:
                </Typography>
                <Typography paragraph>
                  This deliverable is crucial for applying your human expertise
                  and critical thinking to the AI-generated proposal from
                  Deliverable 2. It's not about accepting the AI output blindly,
                  but rather about analyzing it thoughtfully and identifying its
                  merits and limitations in a real-world educational context.
                </Typography>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Areas of Critique & Analysis:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Strengths: What are the positive aspects of the AI's proposal? What elements are well-conceived or potentially effective?" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Weaknesses: What are the shortcomings or limitations of the AI's proposal? Where does it fall short or seem impractical?" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Feasibility: How realistic and practical is this proposal to implement in your school or classroom setting? Consider factors like resources, time, staff expertise, and school culture." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Ethical Considerations: Does the proposal raise any ethical concerns? Consider issues like bias, fairness, privacy, or responsible AI usage in education." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Areas for Improvement: Based on your critique, where can the AI's proposal be enhanced, refined, or made more effective? What's missing or needs to be adjusted?" />
                  </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Educator's Role:
                </Typography>
                <Typography paragraph>
                  This is where your professional judgment and contextual
                  understanding are paramount. The AI provides a starting point,
                  but your critique makes it relevant and grounded in
                  educational reality.
                </Typography>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Key Outcome:
                </Typography>
                <Typography>
                  By the end of this deliverable, you will have a thorough
                  critique and analysis that highlights both the potential and
                  the limitations of the AI-generated proposal, paving the way
                  for human-led refinement.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              // expanded={expandedDeliverable === "deliverable4"}
              expanded={expandedAll || expandedDeliverable === "deliverable4"}
              onChange={handleDeliverableChange("deliverable4")}
              sx={{ mb: 2 }}
              className="pdf-section"
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="deliverable4-content"
                id="deliverable4-header"
                sx={{ bgcolor: "#e3f2fd" }}
              >
                <Typography fontWeight="bold">
                  Deliverable 4: Final Educator-Refined Proposal
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Human-Improved: Incorporate insights from your critique, adapt to local context, and refine for practicality." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Length: 500–800 words." />
                  </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Expanded Description:
                </Typography>
                <Typography paragraph>
                  This final deliverable is where you bring everything together
                  to craft your own, educator-refined solution. You will use the
                  insights gained from your critique in Deliverable 3 to enhance
                  and adapt the AI-generated proposal from Deliverable 2. This
                  is about creating a practical, implementable plan that truly
                  addresses your initial problem.
                </Typography>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  How to Refine the Proposal:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Address Weaknesses: Directly address the weaknesses you identified in your critique. Modify or replace elements of the AI proposal to overcome these limitations." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Incorporate Improvements: Actively integrate the areas for improvement you outlined. Add new steps, resources, or strategies to make the solution stronger." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Contextualize for Your Setting: Adapt the proposal to the specific needs, resources, and culture of your school or classroom. Make it realistic and tailored to your environment." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Leverage Your Expertise: Infuse your professional knowledge, teaching experience, and understanding of your students and school community to enrich and strengthen the solution." />
                  </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Focus on Practicality and Actionability:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Ensure your final proposal is something that could realistically be implemented in an educational setting." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Outline clear and actionable steps that can be taken to put your solution into practice." />
                  </ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Key Outcome:
                </Typography>
                <Typography>
                  By the end of this deliverable, you will have a comprehensive
                  and educator-refined proposal that is practical, actionable,
                  and ready to be shared as a valuable contribution to the
                  ThoughtCoCreate event and the wider educational community.
                  This is your unique solution, enriched by AI but driven by
                  your expertise.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </AccordionDetails>
        </Accordion>

        <Box className="pdf-section">
          {/* Section 5: Submission Format & Process */}
          <Accordion
            // expanded={expanded === "panel5"}
            expanded={expandedAll || expanded === "panel5"}
            onChange={handleChange("panel5")}
            sx={{ mb: 2 }}
            // className="pdf-section"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel5-content"
              id="panel5-header"
              sx={{ bgcolor: "primary.light", color: "white" }}
            >
              <Box display="flex" alignItems="center">
                <Upload sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  5. Submission Format & Process
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Online Submissions
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="All deliverables must be uploaded in PDF or DOCX format via the Pythaverse submission portal." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Adhere to naming conventions (e.g., [LastName]_Deliverable1.pdf)." />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom fontWeight="bold">
                Platform Tools & Templates
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="We will provide standardized templates for each deliverable to ensure consistency." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="AI usage guidelines, example prompts, and best practices are available under 'Resources' in your Pythaverse dashboard." />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom fontWeight="bold">
                Plagiarism & Originality
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Participants must cite AI outputs where appropriate (e.g., referencing which tool was used)." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="While AI can assist, the final deliverables should reflect genuine educator insight and context." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 6: Judging & Evaluation */}
          <Accordion
            // expanded={expanded === "panel6"}
            expanded={expandedAll || expanded === "panel6"}
            onChange={handleChange("panel6")}
            sx={{ mb: 2 }}
            // className="pdf-section"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel6-content"
              id="panel6-header"
              sx={{ bgcolor: "primary.light", color: "white" }}
            >
              <Box display="flex" alignItems="center">
                <EmojiEvents sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  6. Judging & Evaluation
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Judging Committee
              </Typography>
              <Typography paragraph>
                Led by Trung, the committee includes education leaders, AI
                experts, and cross-industry professionals.
              </Typography>

              <Typography variant="h6" gutterBottom fontWeight="bold">
                Evaluation Criteria
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Relevance & Problem Clarity (Does the statement address a genuine, pressing educational challenge in the AI era?)" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Innovation & Practicality (Is the solution creative yet feasible within a typical school setting?)" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Critique Depth (Does the critique effectively analyze the AI-generated proposal's strengths and weaknesses?)" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Refinement Quality (Does the final proposal demonstrate thoughtful human intervention and contextual adaptation?)" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Overall Impact (Potential to transform classrooms, improve learning outcomes, or inspire further innovation.)" />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom fontWeight="bold">
                Scoring Rubrics
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Each criterion is weighted equally (20% each)." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Judges will provide aggregated feedback; top-scoring submissions will move forward." />
                </ListItem>
              </List>

              <a href={TCCDeliverablesJudgingCriteria} download>
                TCC Competition Judging Criteria.pdf
              </a>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box className="pdf-section">
          {/* Section 7: Selection of Finalists & Summit */}
          <Accordion
            // expanded={expanded === "panel7"}
            expanded={expandedAll || expanded === "panel7"}
            onChange={handleChange("panel7")}
            sx={{ mb: 2 }}
            // className="pdf-section"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel7-content"
              id="panel7-header"
              sx={{ bgcolor: "primary.light", color: "white" }}
            >
              <Box display="flex" alignItems="center">
                <School sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  7. Selection of Finalists & Summit
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Top 10 Finalists
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Announced by 1 June 2025." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Invited to the Pythaverse Educators Summit in June for workshops, collaboration, and co-creation." />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom fontWeight="bold">
                Participation in the Summit
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Includes interactive sessions, mentorship, and networking with thought leaders." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Finalists will help refine a Whitepaper that consolidates best practices and practical insights." />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom fontWeight="bold">
                Additional Recognition
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Certificates of Participation for all who submit complete deliverables." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Special acknowledgment of finalists in post-event publications and Pythaverse resources." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 8: The Top 10 Finalist */}
          <Accordion
            // expanded={expanded === "panel8"}
            expanded={expandedAll || expanded === "panel8"}
            onChange={handleChange("panel8")}
            sx={{ mb: 2 }}
            // className="pdf-section"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel8-content"
              id="panel8-header"
              sx={{ bgcolor: "primary.light", color: "white" }}
            >
              <Box display="flex" alignItems="center">
                <School sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  8. The Top 10 Finalist
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                The Top 10 Finalists
              </Typography>
              <Typography paragraph>
                Educators whose entries qualify for the ThoughtCoCreate Top 10 will receive an invitation to present their work at the ThoughtCoCreate Summit. At the Summit, these finalists will present their refined solutions to a panel of judges. Therefore, finalists should prepare a well-organized, clear, and concise presentation, including appropriate audio-visual aids, for a clear delivery to the judges. Each presentation will be a maximum of 10 minutes, followed by a 5-minute question-and-answer session.
              </Typography>
              <Typography paragraph>
                Presentations will take place from 9:00 AM to 12:00 PM (Vietnam time) on July 5th. The announcement and awarding of winners will begin at 1:30 PM (Vietnam time) on the same day.
              </Typography>
              <Typography>
                Event: ThoughtCoCreate Sumit
              </Typography>
              <Typography>
                Venue: Sapa Vietnam
              </Typography>
              <Typography>
                Date: July 5, 2025
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Section 9: Code of Conduct */}
          <Accordion
            // expanded={expanded === "panel9"}
            expanded={expandedAll || expanded === "panel9"}
            onChange={handleChange("panel9")}
            sx={{ mb: 2 }}
            // className="pdf-section"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel9-content"
              id="panel9-header"
              sx={{ bgcolor: "primary.light", color: "white" }}
            >
              <Box display="flex" alignItems="center">
                <Gavel sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  9. Code of Conduct
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Respect & Professionalism: Engage in constructive discussions; personal attacks or discriminatory remarks will not be tolerated." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Data Privacy: Personal information, student data, or any sensitive content must be anonymized unless explicit consent is obtained." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Ethical AI Usage: Use AI responsibly, avoiding hate speech, copyrighted materials, or unethical uses." />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 9: Important Disclaimers */}
          <Accordion
            // expanded={expanded === "panel10"}
            expanded={expandedAll || expanded === "panel10"}
            onChange={handleChange("panel10")}
            sx={{ mb: 2 }}
            // className="pdf-section"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel10-content"
              id="panel10-header"
              sx={{ bgcolor: "primary.light", color: "white" }}
            >
              <Box display="flex" alignItems="center">
                <Warning sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  10. Important Disclaimers
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Modification Rights
              </Typography>
              <Typography paragraph>
                We reserve the right to modify deadlines, rules, or judging
                processes under special circumstances (e.g., platform
                disruptions or force majeure).
              </Typography>

              <Typography variant="h6" gutterBottom fontWeight="bold">
                Intellectual Property
              </Typography>
              <Typography paragraph>
                Participants own the rights to their original content. By
                submitting, participants grant Pythaverse a non-exclusive
                license to publish or showcase their work in the context of this
                event and related marketing.
              </Typography>

              <Typography variant="h6" gutterBottom fontWeight="bold">
                Financial Responsibility
              </Typography>
              <Typography paragraph>
                Travel and accommodation for in-person Summit attendance may be
                partially subsidized but is ultimately the responsibility of
                participants unless otherwise noted.
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Section 11: Contact & Support */}
          <Accordion
            // expanded={expanded === "panel11"}
            expanded={expandedAll || expanded === "panel11"}
            onChange={handleChange("panel11")}
            sx={{ mb: 4 }}
            // className="pdf-section"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel11-content"
              id="panel11-header"
              sx={{ bgcolor: "primary.light", color: "white" }}
            >
              <Box display="flex" alignItems="center">
                <ContactSupport sx={{ mr: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  11. Contact & Support
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Event Inquiries"
                    secondary="For general questions, contact Trinh (Marketing & Outreach Lead) at [placeholder email]."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Technical Support"
                    secondary="Linh (Tech Support) is available through the Pythaverse helpdesk and email."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Platform Issues"
                    secondary="Giang (Platform Administrator) will oversee user access, data security, and platform stability."
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Card sx={{ mb: 4, bgcolor: "#e8f5e9" }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Info color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                Next Steps & Finalization
              </Typography>
            </Box>
            <Typography paragraph>
              This is a draft of the Rules & Guidelines. The final version will
              be published on February 5, 2025 (Event Announcement).
            </Typography>
            <Typography>
              Please share any feedback, edits, or suggestions by [proposed
              date] so we can incorporate them into the finalized document.
            </Typography>
          </CardContent>
        </Card>
      </div>

      <Box textAlign="center" mt={6}>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          color="primary"
          sx={{ textTransform: "none" }}
        >
          Go to Home page
        </Button>
        {/* <Button
          variant="contained"
          color="secondary"
          onClick={handleExportPDF}
          sx={{
            ml: 2,
          }}
        >
          Export to PDF
        </Button> */}
        {/* <Button
          variant="contained"
          size="large"
          color="primary"
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontSize: "1.1rem",
          }}
        >
          Register Now
        </Button>
        <Typography variant="body2" color="text.secondary" mt={2}>
          By registering, you agree to abide by all competition rules and guidelines.
        </Typography> */}
      </Box>
    </Container>
  );
}
